import { useState, useEffect, useRef } from 'react';
import { BaseDialog } from '@app/components';
import emptyLogo from '@app/assets/images/empty-logo.png';
import { reviewTemplate } from '@app/components/EmailTemplateEditor/templates';
import clsx from 'clsx';
import { useAppDispatch } from '@app/redux/hooks';
import { updateFormData } from '@app/redux/features/campaignForm';

interface EmailTemplateEditorProps {
  open: boolean;
  onClose: () => void;
  onSave: (template: string, editedHtml: string) => void;
  initialTemplate?: string;
}

interface ImageSize {
  width: number;
  height: number;
}

export const EmailTemplateEditor = ({
  open,
  onClose,
  onSave,
  initialTemplate = reviewTemplate,
}: EmailTemplateEditorProps) => {
  const dispatch = useAppDispatch();
  const [template, setTemplate] = useState(initialTemplate);
  const [editedHtml, setEditedHtml] = useState(initialTemplate);
  const [previewMode, setPreviewMode] = useState(false);
  const [fontColor, setFontColor] = useState('#000000');
  const [buttonColor, setButtonColor] = useState('#0000ff');
  const [bottomTextColor, setBottomTextColor] = useState('#666666');
  const [fontFamily, setFontFamily] = useState('Arial');
  const [fontSize, setFontSize] = useState(16);
  const [bottomFontSize, setBottomFontSize] = useState(12);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [imageSize, setImageSize] = useState<ImageSize>({ width: 200, height: 100 });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Extract initial styles from template
  useEffect(() => {
    // Extract font color from inline style
    const fontColorMatch = template.match(/<div class="mainText"[^>]*style="[^"]*color:\s*([^;"]+)/);
    if (fontColorMatch) {
      setFontColor(fontColorMatch[1]);
    }

    // Extract button color
    const buttonColorMatch = template.match(/\.button\s*{[^}]*background-color:\s*([^;]+);/);
    if (buttonColorMatch) {
      setButtonColor(buttonColorMatch[1]);
    }

    // Extract bottom text color from inline style
    const bottomTextColorMatch = template.match(/<div class="footer"[^>]*style="[^"]*color:\s*([^;"]+)/);
    if (bottomTextColorMatch) {
      setBottomTextColor(bottomTextColorMatch[1]);
    }

    // Extract font family
    const fontMatch = template.match(/font-family:\s*([^,]+)/);
    if (fontMatch) {
      setFontFamily(fontMatch[1].trim());
    }

    // Extract font size from inline style
    const fontSizeMatch = template.match(/<div class="mainText"[^>]*style="[^"]*font-size:\s*(\d+)px/);
    if (fontSizeMatch) {
      setFontSize(parseInt(fontSizeMatch[1]));
    }

    // Extract bottom font size from inline style
    const bottomFontSizeMatch = template.match(/<div class="footer"[^>]*style="[^"]*font-size:\s*(\d+)px/);
    if (bottomFontSizeMatch) {
      setBottomFontSize(parseInt(bottomFontSizeMatch[1]));
    }

    // Extract logo if it exists
    const logoMatch = template.match(/src="([^"]+)"/);
    if (logoMatch && logoMatch[1] !== '{{businessLogo}}') {
      setLogoPreview(logoMatch[1]);
    }

    // Extract image size if it exists
    const widthMatch = template.match(/width="(\d+)"/);
    const heightMatch = template.match(/height="(\d+)"/);
    if (widthMatch && heightMatch) {
      setImageSize({
        width: parseInt(widthMatch[1]),
        height: parseInt(heightMatch[1]),
      });
    }
  }, [template]);

  // Update editedHtml whenever template changes
  useEffect(() => {
    setEditedHtml(template);
  }, [template]);

  // Load saved options when modal opens
  useEffect(() => {
    if (open) {
      const storedData = localStorage.getItem('campaignFormData');
      if (storedData) {
        try {
          const parsedData = JSON.parse(storedData);
          if (parsedData.emailTemplateOptions) {
            const options = parsedData.emailTemplateOptions;
            setFontColor(options.fontColor || '#000000');
            setButtonColor(options.buttonColor || '#0000ff');
            setBottomTextColor(options.bottomTextColor || '#666666');
            setFontFamily(options.fontFamily || 'Arial');
            setFontSize(options.fontSize || 16);
            setBottomFontSize(options.bottomFontSize || 12);
            setLogoPreview(options.logoPreview || null);
            setImageSize(options.imageSize || { width: 200, height: 100 });
            setTemplate(parsedData.updatedEmailTemplate || template);
          }
        } catch (error) {
          // If parsing fails, keep default values
        }
      }
    }
  }, [open]);

  const handleSave = () => {
    // Save to localStorage immediately
    const storedData = localStorage.getItem('campaignFormData');
    const parsedData = storedData ? JSON.parse(storedData) : {};
    
    // Preserve existing form data and only update template-related fields
    const updatedData = {
      ...parsedData,
      updatedEmailTemplate: editedHtml,
      emailTemplateOptions: {
        fontColor,
        buttonColor,
        bottomTextColor,
        fontFamily,
        fontSize,
        bottomFontSize,
        logoPreview,
        imageSize,
      }
    };
    
    localStorage.setItem('campaignFormData', JSON.stringify(updatedData));
    
    // Dispatch to Redux
    dispatch(updateFormData(updatedData));
    
    onSave(template, editedHtml);
    onClose();
  };

  const handleFontColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    setFontColor(newColor);
    let newTemplate = template;
    
    // Update mainText div and all its text elements
    newTemplate = newTemplate.replace(
      /<div class="mainText"[^>]*>([\s\S]*?)<\/div>/g,
      (_match: string, content: string) => {
        // Add color to all text elements
        const styledContent = content.replace(
          /<([ph])([^>]*)>/g,
          (_tagMatch: string, tag: string, attrs: string) => {
            const style = attrs.includes('style=') 
              ? attrs.replace(/style="([^"]*)"/, `style="$1; color: ${newColor};"`)
              : `${attrs} style="color: ${newColor};"`;
            return `<${tag}${style}>`;
          }
        );
        return `<div class="mainText" style="color: ${newColor};">${styledContent}</div>`;
      }
    );
    
    setTemplate(newTemplate);
  };

  const handleButtonColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    setButtonColor(newColor);
    let newTemplate = template;
    
    // Update button background color in style tag and ensure link color is white
    newTemplate = newTemplate.replace(
      /<style[^>]*>([\s\S]*?)<\/style>/g,
      (_match: string, content: string) => {
        return `<style>
          ${content.replace(/\.button\s*{[^}]*}/, `.button { background-color: ${newColor}; padding: 10px 20px; border-radius: 5px; color: #ffffff !important; text-decoration: none !important; display: inline-block; }`)}
        </style>`;
      }
    );

    // Also update any inline styles on the button link
    newTemplate = newTemplate.replace(
      /<a[^>]*class="button"[^>]*>/g,
      (match) => {
        if (match.includes('style=')) {
          return match.replace(/style="([^"]*)"/, `style="$1; color: #ffffff !important;"`);
        }
        return match.replace(/<a/, '<a style="color: #ffffff !important;"');
      }
    );
    
    setTemplate(newTemplate);
  };

  const handleBottomTextColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    setBottomTextColor(newColor);
    let newTemplate = template;
    
    // Update footer div and all its text elements
    newTemplate = newTemplate.replace(
      /<div class="footer"[^>]*>([\s\S]*?)<\/div>/g,
      (_match: string, content: string) => {
        // Add color to all text elements
        const styledContent = content.replace(
          /<([pa])([^>]*)>/g,
          (_tagMatch: string, tag: string, attrs: string) => {
            const style = attrs.includes('style=') 
              ? attrs.replace(/style="([^"]*)"/, `style="$1; color: ${newColor};"`)
              : `${attrs} style="color: ${newColor};"`;
            return `<${tag}${style}>`;
          }
        );
        return `<div class="footer" style="color: ${newColor};">${styledContent}</div>`;
      }
    );
    
    setTemplate(newTemplate);
  };

  const handleFontFamilyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newFont = e.target.value;
    setFontFamily(newFont);
    let newTemplate = template;
    
    // Update font-family in the style tag and all text elements
    newTemplate = newTemplate.replace(
      /<style[^>]*>([\s\S]*?)<\/style>/g,
      (_match: string, _content: string) => {
        return `<style>
          body { font-family: ${newFont}, sans-serif; }
          .button { background-color: ${buttonColor}; padding: 10px 20px; border-radius: 5px; color: white; text-decoration: none; display: inline-block; }
        </style>`;
      }
    );

    // Update font-family in all text elements
    newTemplate = newTemplate.replace(
      /<div class="mainText"[^>]*>([\s\S]*?)<\/div>/g,
      (_match: string, content: string) => {
        const styledContent = content.replace(
          /<([ph])([^>]*)>/g,
          (_tagMatch: string, tag: string, attrs: string) => {
            const style = attrs.includes('style=') 
              ? attrs.replace(/style="([^"]*)"/, `style="$1; font-family: ${newFont}, sans-serif;"`)
              : `${attrs} style="font-family: ${newFont}, sans-serif;"`;
            return `<${tag}${style}>`;
          }
        );
        return `<div class="mainText" style="font-family: ${newFont}, sans-serif;">${styledContent}</div>`;
      }
    );

    // Update font-family in footer elements
    newTemplate = newTemplate.replace(
      /<div class="footer"[^>]*>([\s\S]*?)<\/div>/g,
      (_match: string, content: string) => {
        const styledContent = content.replace(
          /<([pa])([^>]*)>/g,
          (_tagMatch: string, tag: string, attrs: string) => {
            const style = attrs.includes('style=') 
              ? attrs.replace(/style="([^"]*)"/, `style="$1; font-family: ${newFont}, sans-serif;"`)
              : `${attrs} style="font-family: ${newFont}, sans-serif;"`;
            return `<${tag}${style}>`;
          }
        );
        return `<div class="footer" style="font-family: ${newFont}, sans-serif;">${styledContent}</div>`;
      }
    );
    
    setTemplate(newTemplate);
  };

  const handleFontSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSize = parseInt(e.target.value);
    setFontSize(newSize);
    let newTemplate = template;
    
    // Update mainText div and all its text elements
    newTemplate = newTemplate.replace(
      /<div class="mainText"[^>]*>([\s\S]*?)<\/div>/g,
      (_match: string, content: string) => {
        // Add font size to all text elements
        const styledContent = content.replace(
          /<([ph])([^>]*)>/g,
          (_tagMatch: string, tag: string, attrs: string) => {
            const style = attrs.includes('style=') 
              ? attrs.replace(/style="([^"]*)"/, `style="$1; font-size: ${newSize}px !important;"`)
              : `${attrs} style="font-size: ${newSize}px !important;"`;
            return `<${tag}${style}>`;
          }
        );
        return `<div class="mainText" style="font-size: ${newSize}px !important;">${styledContent}</div>`;
      }
    );
    
    setTemplate(newTemplate);
  };

  const handleBottomFontSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSize = parseInt(e.target.value);
    setBottomFontSize(newSize);
    let newTemplate = template;
    
    // Update footer div and all its text elements
    newTemplate = newTemplate.replace(
      /<div class="footer"[^>]*>([\s\S]*?)<\/div>/g,
      (_match: string, content: string) => {
        // Add font size to all text elements
        const styledContent = content.replace(
          /<([pa])([^>]*)>/g,
          (_tagMatch: string, tag: string, attrs: string) => {
            const style = attrs.includes('style=') 
              ? attrs.replace(/style="([^"]*)"/, `style="$1; font-size: ${newSize}px !important;"`)
              : `${attrs} style="font-size: ${newSize}px !important;"`;
            return `<${tag}${style}>`;
          }
        );
        return `<div class="footer" style="font-size: ${newSize}px !important;">${styledContent}</div>`;
      }
    );
    
    setTemplate(newTemplate);
  };

  const handleImageSizeChange = (dimension: 'width' | 'height', value: number) => {
    const newSize = { ...imageSize, [dimension]: value };
    setImageSize(newSize);
    
    let newTemplate = template;
    
    // Update the img tag with inline styles
    if (template.includes('class="business-logo"')) {
      newTemplate = newTemplate.replace(
        /<img[^>]*class="business-logo"[^>]*>/,
        `<img class="business-logo" style="width: ${newSize.width}px; height: ${newSize.height}px; display: block; border: 0;" src="${template.match(/src="([^"]+)"/)?.[1] || '{{businessLogo}}'}" alt="Business Logo">`
      );
    } else {
      // Find the img tag and add inline styles
      newTemplate = newTemplate.replace(
        /<img[^>]*src="[^"]*"[^>]*>/,
        (match) => {
          const src = match.match(/src="([^"]+)"/)?.[1] || '{{businessLogo}}';
          return `<img class="business-logo" style="width: ${newSize.width}px; height: ${newSize.height}px; display: block; border: 0;" src="${src}" alt="Business Logo">`;
        }
      );
    }
    
    setTemplate(newTemplate);
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        // Get the file extension and MIME type
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        const mimeType = file.type || `image/${fileExtension}`;
        
        // Format the base64 string with proper MIME type
        const formattedBase64 = base64String.replace(/^data:.*?;base64,/, '');
        const imageDataUrl = `data:${mimeType};base64,${formattedBase64}`;
        
        setLogoPreview(imageDataUrl);
        
        let newTemplate = template;
        
        // Update the img tag with inline styles
        if (template.includes('src="{{businessLogo}}"')) {
          newTemplate = newTemplate.replace(
            /<img[^>]*src="{{businessLogo}}"[^>]*>/,
            `<img class="business-logo" style="width: ${imageSize.width}px; height: ${imageSize.height}px; display: block; border: 0;" src="${imageDataUrl}" alt="Business Logo">`
          );
        } else {
          newTemplate = newTemplate.replace(
            /<img[^>]*src="[^"]*"[^>]*>/,
            `<img class="business-logo" style="width: ${imageSize.width}px; height: ${imageSize.height}px; display: block; border: 0;" src="${imageDataUrl}" alt="Business Logo">`
          );
        }
        
        setTemplate(newTemplate);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (fileInputRef.current) {
      fileInputRef.current.click();
    } else {
      console.error('File input ref is not available');
    }
  };

  const handleRemoveLogo = () => {
    setLogoPreview(null);
    let newTemplate = template;
    
    // Update the img tag with inline styles for the placeholder
    newTemplate = newTemplate.replace(
      /<img[^>]*src="[^"]*"[^>]*>/,
      `<img class="business-logo" style="width: ${imageSize.width}px; height: ${imageSize.height}px; display: block; border: 0;" src="{{businessLogo}}" alt="Business Logo">`
    );
    
    setTemplate(newTemplate);
  };

  return (
    <BaseDialog open={open} onClose={onClose} size="max-w-2xl">
      <div className="px-3 pt-4 pb-2 space-y-6 bg-[#fafbfc] rounded-lg max-h-[80vh] overflow-y-auto">
        <div className="flex justify-end gap-2 mb-1">
          <button
            type="button"
            onClick={() => setPreviewMode(!previewMode)}
            className={clsx(
              'px-3 py-1.5 text-sm font-medium rounded-lg transition-colors bg-base-ghostWhite text-base-blue hover:bg-base-ghostWhite/80',
              previewMode
                ? 'border-2 border-brand-baseBlue'
                : ''
            )}
          >
            {previewMode ? 'Edit Mode' : 'Preview Mode'}
          </button>
        </div>
        <h4 className="text-base font-semibold text-gray-800 mb-2">Design settings</h4>

        {previewMode ? (
          <div className="border rounded-lg p-2 bg-white shadow-sm mt-4">
            <iframe
              srcDoc={template}
              title="Email Preview"
              className="w-full h-[500px] border-0"
            />
          </div>
        ) : (
          <>
            {/* Font Type */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Font type</label>
              <select
                value={fontFamily}
                onChange={handleFontFamilyChange}
                className="w-full p-2 border border-gray-200 rounded-lg bg-white text-sm focus:border-brand-baseBlue focus:ring-2 focus:ring-brand-lightBlue4 transition-colors"
              >
                <option value="Arial">Arial</option>
                <option value="Helvetica">Helvetica</option>
                <option value="Georgia">Georgia</option>
                <option value="Times New Roman">Times New Roman</option>
              </select>
            </div>

            {/* Main Text Size */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Main text size (px)</label>
              <input
                type="number"
                value={fontSize}
                onChange={handleFontSizeChange}
                className="w-full p-2 border border-gray-200 rounded-lg bg-white text-sm focus:border-brand-baseBlue focus:ring-2 focus:ring-brand-lightBlue4 transition-colors"
              />
            </div>

            {/* Bottom Text Size */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Bottom text size (px)</label>
              <input
                type="number"
                value={bottomFontSize}
                onChange={handleBottomFontSizeChange}
                className="w-full p-2 border border-gray-200 rounded-lg bg-white text-sm focus:border-brand-baseBlue focus:ring-2 focus:ring-brand-lightBlue4 transition-colors"
              />
            </div>

            {/* Company logo */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Company logo</label>
              {/* File input always present and hidden */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
              />
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleLogoClick}
                  className="w-36 h-28 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer bg-white hover:border-brand-baseBlue transition-colors"
                  style={{ minWidth: '7rem' }}
                >
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt="Business Logo"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <img src={emptyLogo} alt="Empty Logo" className="w-full h-full object-cover" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleLogoClick}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-[#f4f6fa] border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Choose photo
                </button>
                {logoPreview && (
                  <button
                    type="button"
                    onClick={handleRemoveLogo}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors ml-2"
                  >
                    Remove
                  </button>
                )}
              </div>
              {/* Logo size controls */}
              {logoPreview && (
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Width (px)</label>
                    <input
                      type="number"
                      value={imageSize.width}
                      onChange={(e) => handleImageSizeChange('width', parseInt(e.target.value))}
                      className="w-full p-2 border border-gray-200 rounded-lg bg-white text-sm focus:border-brand-baseBlue focus:ring-2 focus:ring-brand-lightBlue4 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Height (px)</label>
                    <input
                      type="number"
                      value={imageSize.height}
                      onChange={(e) => handleImageSizeChange('height', parseInt(e.target.value))}
                      className="w-full p-2 border border-gray-200 rounded-lg bg-white text-sm focus:border-brand-baseBlue focus:ring-2 focus:ring-brand-lightBlue4 transition-colors"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Color Pickers Grouped at Bottom */}
            <div className="space-y-4 mt-4">
              {/* Text Color Picker */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Text color</label>
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="color"
                    value={fontColor}
                    onChange={handleFontColorChange}
                    className="w-10 h-10 p-1 border border-gray-300 rounded-lg focus:border-brand-baseBlue focus:ring-2 focus:ring-brand-lightBlue4 transition-colors"
                  />
                  <input
                    type="text"
                    value={fontColor}
                    onChange={handleFontColorChange}
                    className="w-24 p-1 border border-gray-200 rounded-lg text-xs bg-white focus:border-brand-baseBlue focus:ring-2 focus:ring-brand-lightBlue4 transition-colors"
                  />
                </div>
                <div className="flex gap-2 mt-1">
                  {['#000000','#666666','#00B2FF','#FFD600','#FF6B00','#FF3B30','#FF2D55','#34C759','#5856D6','#FF9500','#FFCC00','#5AC8FA','#FF5E3A','#C7C7CC'].map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={clsx(
                        'w-6 h-6 rounded-full border-2',
                        fontColor === color ? 'border-brand-baseBlue' : 'border-gray-200'
                      )}
                      style={{ backgroundColor: color }}
                      onClick={() => handleFontColorChange({ target: { value: color } } as any)}
                      aria-label={color}
                    />
                  ))}
                </div>
              </div>

              {/* Button color */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Button color</label>
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="color"
                    value={buttonColor}
                    onChange={handleButtonColorChange}
                    className="w-10 h-10 p-1 border border-gray-300 rounded-lg focus:border-brand-baseBlue focus:ring-2 focus:ring-brand-lightBlue4 transition-colors"
                  />
                  <input
                    type="text"
                    value={buttonColor}
                    onChange={handleButtonColorChange}
                    className="w-24 p-1 border border-gray-200 rounded-lg text-xs bg-white focus:border-brand-baseBlue focus:ring-2 focus:ring-brand-lightBlue4 transition-colors"
                  />
                </div>
                <div className="flex gap-2 mt-1">
                  {['#000000','#666666','#00B2FF','#FFD600','#FF6B00','#FF3B30','#FF2D55','#34C759','#5856D6','#FF9500','#FFCC00','#5AC8FA','#FF5E3A','#C7C7CC'].map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={clsx(
                        'w-6 h-6 rounded-full border-2',
                        buttonColor === color ? 'border-brand-baseBlue' : 'border-gray-200'
                      )}
                      style={{ backgroundColor: color }}
                      onClick={() => handleButtonColorChange({ target: { value: color } } as any)}
                      aria-label={color}
                    />
                  ))}
                </div>
              </div>

              {/* Bottom Text Color */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Bottom text color</label>
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="color"
                    value={bottomTextColor}
                    onChange={handleBottomTextColorChange}
                    className="w-10 h-10 p-1 border border-gray-300 rounded-lg focus:border-brand-baseBlue focus:ring-2 focus:ring-brand-lightBlue4 transition-colors"
                  />
                  <input
                    type="text"
                    value={bottomTextColor}
                    onChange={handleBottomTextColorChange}
                    className="w-24 p-1 border border-gray-200 rounded-lg text-xs bg-white focus:border-brand-baseBlue focus:ring-2 focus:ring-brand-lightBlue4 transition-colors"
                  />
                </div>
                <div className="flex gap-2 mt-1">
                  {['#000000','#666666','#00B2FF','#FFD600','#FF6B00','#FF3B30','#FF2D55','#34C759','#5856D6','#FF9500','#FFCC00','#5AC8FA','#FF5E3A','#C7C7CC'].map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={clsx(
                        'w-6 h-6 rounded-full border-2',
                        bottomTextColor === color ? 'border-brand-baseBlue' : 'border-gray-200'
                      )}
                      style={{ backgroundColor: color }}
                      onClick={() => handleBottomTextColorChange({ target: { value: color } } as any)}
                      aria-label={color}
                    />
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Preview and Save/Cancel buttons */}
        <div className="flex justify-end gap-3 mt-6 pt-2 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 text-sm font-medium bg-base-ghostWhite text-base-blue rounded-lg hover:bg-base-ghostWhite/80 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-4 py-1.5 text-sm font-medium text-white bg-brand-baseBlue rounded-lg hover:bg-brand-darkBlue transition-colors"
          >
            Save Template
          </button>
        </div>
      </div>
    </BaseDialog>
  );
}; 